import React from 'react';
import Text from '@amzn/meridian/text';
import DisplaySubMetric from './DisplaySubMetric';
import Row from '@amzn/meridian/row';
import { FormattedMessage } from 'react-intl';
import { DisplaySubMetricsProps } from '../types';
import {
  SUBMETRIC_FONT_SIZE,
  SUBMETRIC_ROW_WIDTHS,
  UnderstandabilityDataCheck,
} from '../helper';
import { ControlledExpander } from './Util/ControlledExpander';
import Column from '@amzn/meridian/column';
import NoSubMetric from './Util/NoSubMetric';

const DisplayUnderstandabilitySubMetrics: React.FC<DisplaySubMetricsProps> = ({
  metrics,
  cscEntityPrefix,
}: DisplaySubMetricsProps): React.ReactElement => {
  if (!metrics.UNDERSTANDABILITY?.submetrics) {
    return <></>;
  }
  if (UnderstandabilityDataCheck(metrics.UNDERSTANDABILITY?.submetrics!)) {
    return <NoSubMetric />;
  }
  const { attentive, friendliness, knowledgeable, ccxOverallService } =
    metrics.UNDERSTANDABILITY?.submetrics!;

  return (
    <Column spacing='none' width='100%'>
      {attentive && cscEntityPrefix !== 'cscentral_eu_user' && (
        <Row
          alignmentVertical='top'
          width='100%'
          widths={SUBMETRIC_ROW_WIDTHS}
          spacing='none'
          spacingInset='none none 200'
        >
          <ControlledExpander
            title={
              <Text
                type={SUBMETRIC_FONT_SIZE}
                className='lighthouse-submetric-long-word-treatment'
              >
                <FormattedMessage
                  id='cs_perfportal_lighthouse_attentive'
                  defaultMessage='Attentive'
                />
              </Text>
            }
            type='list'
          >
            <Text
              type={SUBMETRIC_FONT_SIZE}
              className='lighthouse-submetric-long-word-treatment'
            >
              <FormattedMessage
                id='cs_perfportal_lighthouse_attentive_def'
                defaultMessage='Percentage of 4 to 5-star responses to the “Attentive” CCX rating.'
              />
            </Text>
          </ControlledExpander>
          <DisplaySubMetric value={attentive.datapoint} isPercentage isBold />
          <DisplaySubMetric
            value={attentive.networkAverage}
            isPercentage
            datapointNull={attentive.datapoint === null}
          />
        </Row>
      )}
      {friendliness && (
        <Row
          alignmentVertical='top'
          width='100%'
          widths={SUBMETRIC_ROW_WIDTHS}
          spacing='none'
          spacingInset='none none 200'
        >
          <ControlledExpander
            title={
              <Text
                type={SUBMETRIC_FONT_SIZE}
                className='lighthouse-submetric-long-word-treatment'
              >
                <FormattedMessage
                  id='cs_perfportal_lighthouse_friendliness'
                  defaultMessage='Friendliness'
                />
              </Text>
            }
            type='list'
          >
            <Text type={SUBMETRIC_FONT_SIZE}>
              <FormattedMessage
                id='cs_perfportal_lighthouse_friendliness_def'
                defaultMessage='Percentage of 4 to 5-star responses to the “Friendly” CCX rating.'
              />
            </Text>
          </ControlledExpander>
          <DisplaySubMetric
            value={friendliness.datapoint}
            isPercentage
            isBold
          />
          <DisplaySubMetric
            value={friendliness.networkAverage!}
            isPercentage
            datapointNull={friendliness.datapoint === null}
          />
        </Row>
      )}
      {knowledgeable && (
        <Row
          alignmentVertical='top'
          width='100%'
          widths={SUBMETRIC_ROW_WIDTHS}
          spacing='none'
          spacingInset='none none 200'
        >
          <ControlledExpander
            title={
              <Text
                type={SUBMETRIC_FONT_SIZE}
                className='lighthouse-submetric-long-word-treatment'
              >
                <FormattedMessage
                  id='cs_perfportal_lighthouse_knowledgeable'
                  defaultMessage='Knowledgeable'
                />
              </Text>
            }
            type='list'
          >
            <Text type={SUBMETRIC_FONT_SIZE}>
              <FormattedMessage
                id='cs_perfportal_lighthouse_knowledgeable_def'
                defaultMessage='Percentage of 4 to 5-star responses to the “Knowledgeable” CCX rating.'
              />
            </Text>
          </ControlledExpander>
          <DisplaySubMetric
            value={knowledgeable.datapoint}
            isPercentage
            isBold
          />
          <DisplaySubMetric
            value={knowledgeable.networkAverage!}
            isPercentage
            datapointNull={knowledgeable.datapoint === null}
          />
        </Row>
      )}
      {/* We might introduce flag that make sure Reassuring is displayed in EU and Attentivee is Displayed in other regions */}
      {attentive && cscEntityPrefix === 'cscentral_eu_user' && (
        <Row
          alignmentVertical='top'
          width='100%'
          widths={SUBMETRIC_ROW_WIDTHS}
          spacing='none'
          spacingInset='none none 200'
        >
          <ControlledExpander
            title={
              <Text
                type={SUBMETRIC_FONT_SIZE}
                className='lighthouse-submetric-long-word-treatment'
              >
                <FormattedMessage
                  id='cs_perfportal_lighthouse_reassuring'
                  defaultMessage='Reassuring'
                />
              </Text>
            }
            type='list'
          >
            <Text
              type={SUBMETRIC_FONT_SIZE}
              className='lighthouse-submetric-long-word-treatment'
            >
              <FormattedMessage
                id='cs_perfportal_lighthouse_reassuring_def'
                defaultMessage='Percentage of 4 to 5-star responses to the “Reassuring” CCX rating.'
              />
            </Text>
          </ControlledExpander>
          <DisplaySubMetric value={attentive.datapoint} isPercentage isBold />
          <DisplaySubMetric
            value={attentive.networkAverage}
            isPercentage
            datapointNull={attentive.datapoint === null}
          />
        </Row>
      )}
      {ccxOverallService && (
        <Row
          alignmentVertical='top'
          width='100%'
          widths={SUBMETRIC_ROW_WIDTHS}
          spacing='none'
          spacingInset='none none 200'
        >
          <ControlledExpander
            title={
              <Text
                type={SUBMETRIC_FONT_SIZE}
                className='lighthouse-submetric-long-word-treatment'
              >
                <FormattedMessage
                  id='cs_perfportal_lighthouse_overService'
                  defaultMessage='Overall Service (Helpfulness)'
                />
              </Text>
            }
            type='list'
          >
            <Text type={SUBMETRIC_FONT_SIZE}>
              <FormattedMessage
                id='cs_perfportal_lighthouse_overService_def'
                defaultMessage="Percentage of 4 to 5-star responses to the 'Overall Service' Customer Contact Experience (CCX) rating. This rating is also known as 'Helpfulness'."
              />
            </Text>
          </ControlledExpander>
          <DisplaySubMetric
            value={ccxOverallService.datapoint}
            isPercentage
            isBold
          />
          <DisplaySubMetric
            value={ccxOverallService.networkAverage}
            isPercentage
            datapointNull={ccxOverallService.datapoint === null}
          />
        </Row>
      )}
    </Column>
  );
};

export default DisplayUnderstandabilitySubMetrics;
